import React, {useState, useEffect} from 'react';
import {fetchSurveyUrls} from '../api';
import {handleExternalUrl} from '../utils';
import {globalStyle, styles} from '../styles';
import {translationService} from '../services/translationService';

export function Home() {
    const [surveys, setSurveys] = useState([]);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [translations, setTranslations] = useState({});

    useEffect(() => {
        const initializeTranslations = async () => {
            try {
                const userLocale = navigator.language.split(/[-_]/)[0];
                console.log('Current locale:', userLocale); // Debug log

                await translationService.loadTranslations(userLocale);
                const currentMessages = translationService.messages[userLocale];

                if (currentMessages) {
                    setTranslations(currentMessages);
                } else {
                    console.error('No translations found for locale:', userLocale);
                    await translationService.loadTranslations('en');
                    setTranslations(translationService.messages['en']);
                }
            } catch (error) {
                console.error('Failed to load translations:', error);
                // Fallback to English
                await translationService.loadTranslations('en');
                setTranslations(translationService.messages['en']);
            }
        };

        initializeTranslations();
    }, []);

    useEffect(() => {
        const loadSurveys = async () => {
            try {
                setIsLoading(true);
                const data = await fetchSurveyUrls();
                setSurveys(data);
                setError(null);
            } catch (error) {
                console.error('Error in component:', error);
                const translatedErrorMessage = translationService.getErrorMessage(error);
                setError(translationService.translate('failedToFetch', translatedErrorMessage));
            } finally {
                setIsLoading(false);
            }
        };

        loadSurveys();
    }, []);


    const handleLinkClick = (event, url) => {
        event.preventDefault();
        handleExternalUrl(url);
    };

    const handleKeyDown = (event, url) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleExternalUrl(url);
        }
    };

    const t = key => translations[key] || key;

    return (
        <>
            <style>{globalStyle}</style>
            <div
                role="application"
                aria-label={t('surveyHeader')}
            >
                <main
                    id="main-content"
                    style={styles.pageContainer}
                    role="main"
                >
                    {!isLoading && !error && surveys.length > 0 && (
                        <div
                            style={styles.contentPadding}
                        >
                            <h1
                                style={styles.subHeading}
                                tabIndex="0"  // Make it focusable
                                aria-label={t('surveyHeader')}
                            >
                                {t('surveyHeader')}
                            </h1>
                        </div>
                    )}

                    {isLoading ? (
                        <div
                            style={styles.loadingError}
                            role="status"
                            aria-live="polite"
                            tabIndex="0"  // Make focusable
                        >
                            <p style={styles.messageText}>{translationService.translate('loading')}</p>
                        </div>
                    ) : error ? (
                        <div
                            style={styles.loadingError}
                            role="alert"
                            tabIndex="0"
                        >
                            <p style={styles.messageText}>{error}</p>
                        </div>
                    ) : surveys.length === 0 ? (
                        <div
                            style={styles.loadingError}
                            role="status"
                            tabIndex="0"
                        >
                            <p style={styles.messageText}>{t('noSurveys')}</p>
                        </div>
                    ) : (
                        <nav
                            style={styles.surveyList}
                            aria-label={t('surveyListLabel')}
                            role="navigation"
                        >
                            {surveys.map((survey, index) => (
                                <a
                                    key={index}
                                    href={survey.url}
                                    onClick={(e) => handleLinkClick(e, survey.url)}
                                    onKeyDown={(e) => handleKeyDown(e, survey.url)}
                                    style={{
                                        ...styles.surveyItem,
                                        ':focus': {
                                            outline: '2px solid #4A90E2',
                                            outlineOffset: '2px'
                                        }
                                    }}
                                    aria-label={translationService.translate('takeSurvey', survey.name)}
                                    tabIndex="0"
                                >
                                    {survey.name}
                                </a>
                            ))}
                        </nav>
                    )}
                </main>
            </div>
        </>
    );
}